import React from "react";
import {
  CheckCircleIcon,
  LockClosedIcon,
  XIcon,
} from "@heroicons/react/outline";
import { calculateScoreForSlot } from "../../utils/events/FighterScores";

/**
 * Modal for confirming lineup submission
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when submission is confirmed
 * @param {Array} props.lineup - Array of selected fighters
 * @param {number|null} props.lotn - ID of fighter selected as LOTN
 * @param {Array} props.alternates - Array of alternate fighters
 * @param {number|null} props.alternateLotn - ID of fighter selected as alternate LOTN
 * @param {number} props.projectedTotal - Projected score for the lineup
 * @param {Function} [props.getWeightClass] - Optional function to get weight class for a fighter
 * @returns {JSX.Element}
 */
const SubmissionModal = ({
  isOpen,
  onClose,
  onConfirm,
  lineup,
  lotn,
  alternates,
  alternateLotn,
  projectedTotal,
  getWeightClass,
}) => {
  if (!isOpen) return null;

  // Filter out null values from lineup
  const validLineup = lineup.filter((fighter) => fighter);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div
        className="fixed inset-0 bg-dark-grey bg-opacity-75 transition-opacity"
        onClick={onClose}
      ></div>

      <div className="relative transform overflow-hidden rounded-t-2xl sm:rounded-xl bg-white text-left shadow-2xl transition-all w-full max-w-sm sm:max-w-md mx-auto max-h-[90vh] sm:max-h-[85vh] flex flex-col mb-safe">
        {/* Header */}
        <div className="bg-gradient-to-r from-deep-red to-bright-red text-white p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Confirm Submission</h3>
                <p className="text-white/80 text-sm">
                  Review your lineup before submitting
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-white"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Sticky Projected Score */}
          <div className="sticky top-0 bg-white z-10 border-b border-light-grey">
            <div className="bg-gradient-to-r from-deep-red/5 to-bright-red/5 p-4">
              <div className="text-center">
                <div className="text-xs text-medium-grey mb-1 font-medium uppercase tracking-wide">
                  Projected Score
                </div>
                <div className="text-3xl font-bold text-deep-red">
                  {projectedTotal.toFixed(1)}
                </div>
                <div className="text-xs text-medium-grey font-medium">
                  points
                </div>
              </div>
            </div>
          </div>

          <div className="p-4">
            {/* Primary Lineup */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-deep-red rounded-full"></div>
                <h4 className="text-sm font-bold text-dark-grey">
                  Primary Lineup
                </h4>
                <div className="text-xs text-medium-grey bg-light-grey px-2 py-1 rounded-full">
                  {validLineup.length}/5
                </div>
              </div>
              <div className="space-y-2">
                {validLineup.map((fighter, index) => (
                  <div
                    key={index}
                    className="bg-white border border-light-grey rounded-xl p-3 shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-deep-red text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-dark-grey truncate">
                          {fighter.FirstName} {fighter.LastName}
                        </div>
                        <div className="text-xs text-medium-grey mt-0.5">
                          {getWeightClass
                            ? getWeightClass(fighter.FighterId)
                            : fighter.WeightClass || ""}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {fighter.FighterId === lotn && (
                          <div className="flex items-center gap-1 bg-gold px-2 py-1 rounded-full">
                            <LockClosedIcon className="w-3 h-3 text-dark-grey" />
                            <span className="text-xs font-bold text-dark-grey">
                              LOTN
                            </span>
                          </div>
                        )}
                        {fighter.FighterId === alternateLotn && (
                          <div className="flex items-center gap-1 bg-blue-100 border border-blue-200 px-2 py-1 rounded-full">
                            <LockClosedIcon className="w-3 h-3 text-blue-600" />
                            <span className="text-xs font-bold text-blue-700">
                              Alt LOTN
                            </span>
                          </div>
                        )}
                        <div className="text-right">
                          <div className="text-sm font-bold text-deep-red">
                            {fighter.FighterId === lotn
                              ? (
                                  calculateScoreForSlot(
                                    fighter.Moneyline,
                                    index,
                                  ) * 2
                                ).toFixed(1)
                              : calculateScoreForSlot(
                                  fighter.Moneyline,
                                  index,
                                ).toFixed(1)}
                          </div>
                          <div className="text-xs text-medium-grey">pts</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alternates */}
            {alternates.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-medium-grey rounded-full"></div>
                  <h4 className="text-sm font-bold text-dark-grey">
                    Alternates
                  </h4>
                  <div className="text-xs text-medium-grey bg-light-grey px-2 py-1 rounded-full">
                    {alternates.length}/2
                  </div>
                </div>
                <div className="space-y-2">
                  {alternates.map((fighter, index) => (
                    <div
                      key={index}
                      className="bg-white border border-light-grey rounded-xl p-3 shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-medium-grey text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                          A{index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-dark-grey truncate">
                            {fighter.FirstName} {fighter.LastName}
                          </div>
                          <div className="text-xs text-medium-grey mt-0.5">
                            {getWeightClass
                              ? getWeightClass(fighter.FighterId)
                              : fighter.WeightClass || ""}
                          </div>
                        </div>
                        {fighter.FighterId === alternateLotn && (
                          <div className="flex items-center gap-1 bg-blue-100 border border-blue-200 px-2 py-1 rounded-full flex-shrink-0">
                            <LockClosedIcon className="w-3 h-3 text-blue-600" />
                            <span className="text-xs font-bold text-blue-700">
                              Alt LOTN
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Actions Footer */}
        <div className="p-4 bg-light-grey border-t border-gray-200 flex-shrink-0">
          <div className="space-y-3">
            <button
              type="button"
              onClick={onConfirm}
              className="w-full bg-deep-red hover:bg-bright-red text-white py-4 px-4 rounded-xl text-base font-bold transition-colors min-h-[48px] flex items-center justify-center shadow-sm"
            >
              Confirm Submission
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-white text-dark-grey py-3 px-4 rounded-xl text-sm font-medium border border-light-grey hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-center"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionModal;
